import { classNames } from 'libs/react';
import { useEffect } from 'libs/react';
import { useRef } from 'libs/react';
import { useState } from 'libs/react';
import { useValue } from 'libs/react';
import { React } from 'libs/react';
import './Image.scss';
/**
 * @component Image
 * @since 1.0.0
 */
export function Image(props) {
    var _a;
    const { style, cover, eager, lazy, source } = Object.assign({ style: 'normal', cover: false, eager: false, lazy: false }, props);
    const node = useRef(null);
    const load = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [inside, setInside] = useState(eager);
    const [target, setTarget] = useState('');
    // Lazy images track the source that finished loading rather than a flag:
    // a cached image can load before the reset in the source effect runs.
    const [settled, setSettled] = useState(null);
    const observer = useValue(() => new IntersectionObserver(([e]) => onIntersect(), { threshold: [1] }));
    /**
     * @function watch
     * @since 1.0.0
     * @hidden
     */
    function watch() {
        if (node.current) {
            observer.observe(node.current);
        }
    }
    /**
     * @function unwatch
     * @since 1.0.0
     * @hidden
     */
    function unwatch() {
        if (node.current) {
            observer.unobserve(node.current);
        }
    }
    /**
     * @function onImageLoad
     * @since 1.0.0
     * @hidden
     */
    function onImageLoad() {
        setLoaded(true);
        if (props.onLoad) {
            props.onLoad();
        }
    }
    /**
     * @function onLazySettle
     * @since 1.0.0
     * @hidden
     */
    function onLazySettle(failed) {
        var _a, _b;
        setSettled(source !== null && source !== void 0 ? source : null);
        if (failed) {
            (_a = props.onFail) === null || _a === void 0 ? void 0 : _a.call(props);
        }
        else {
            (_b = props.onLoad) === null || _b === void 0 ? void 0 : _b.call(props);
        }
    }
    /**
     * @function onImageFail
     * @since 1.0.0
     * @hidden
     */
    function onImageFail() {
        setLoaded(true);
        if (props.onFail) {
            props.onFail();
        }
    }
    /**
     * @function onIntersect
     * @since 1.0.0
     * @hidden
     */
    function onIntersect() {
        setInside(true);
        if (props.onEnter) {
            props.onEnter();
        }
        unwatch();
    }
    useEffect(() => {
        setTarget('');
        setInside(eager);
        setLoaded(false);
        unwatch();
        watch();
        if (load.current) {
            load.current();
        }
        if (!eager && !lazy && source) {
            load.current = preload(source, () => onImageLoad(), () => onImageFail());
        }
        return () => {
            if (load.current) {
                load.current();
            }
        };
    }, [source]);
    useEffect(() => {
        if (!eager && source && inside && loaded) {
            setTarget(source);
        }
    }, [inside, loaded]);
    useEffect(() => {
        watch();
        return () => {
            unwatch();
        };
    }, []);
    const ready = lazy ? settled === source : inside && loaded;
    const className = classNames('image', 'image--' + style, { 'image--is-cover': cover }, { 'image--is-eager': eager }, { 'image--is-ready': ready }, props.className);
    return (React.createElement("div", { ref: node, className: className, onClick: props.onClick },
        React.createElement("div", { className: "image-loader" }),
        React.createElement("div", { className: "image-bitmap" }, lazy && source
            ? React.createElement("img", { className: "image-bitmap", src: source, srcSet: props.srcSet, sizes: props.sizes, alt: (_a = props.alt) !== null && _a !== void 0 ? _a : '', loading: "lazy", decoding: "async", onLoad: () => onLazySettle(false), onError: () => onLazySettle(true) })
            : eager && source
                ? React.createElement("img", { className: "image-bitmap", src: source, srcSet: props.srcSet, sizes: props.sizes, alt: props.alt, onLoad: onImageLoad, onError: onImageFail })
                : target && React.createElement("img", { className: "image-bitmap", src: target }))));
}
/**
 * @function preload
 * @since 1.0.0
 * @hidden
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function preload(source, success, failure) {
    success = success || function () { };
    failure = failure || function () { };
    const onAbort = function () {
        failure(image);
    };
    const onError = function () {
        failure(image);
    };
    const onLoad = function () {
        success(image);
    };
    const image = new window.Image();
    image.src = source;
    if (image.complete) {
        onLoad();
    }
    else {
        image.addEventListener('abort', onAbort);
        image.addEventListener('error', onError);
        image.addEventListener('load', onLoad);
    }
    return function () {
        image.removeEventListener('abort', onAbort);
        image.removeEventListener('error', onError);
        image.removeEventListener('load', onLoad);
    };
}
